// AppSplash.js — the co-branded splash when the side panel opens straight
// onto a connected app (a rail button). Opening the panel at a new URL boots
// the whole app (Home → the visit → the app's tab → its frame), and each step
// used to paint. This covers all of that: the Comprehend mark and the
// partner's icon slide together, dots pulse between them, and when the
// partner's frame has loaded and is on screen the pair swells slightly and
// the splash fades into it.
//
// It never adds wait: it lifts as soon as the frame is ready (shown for at
// least MIN_MS so it doesn't flicker), and gives up after MAX_MS whatever
// state things are in.
import { useEffect, useRef, useState } from 'react';
import useVendorStore from '../../utils/vendorApps/useVendorStore';
import { CATALOG_KEY } from '../../utils/workspace/workspaceLayout';
import logo from '../../assets/img/brand/logo.png';

const MIN_MS = 300;
const MAX_MS = 4000;
const OUT_MS = 280;
const STYLE_ID = 'cpt-app-splash-style';

function ensureStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    '@keyframes cpt-splash-left{from{opacity:0;transform:translateX(-26px)}to{opacity:1;transform:none}}' +
    '@keyframes cpt-splash-right{from{opacity:0;transform:translateX(26px)}to{opacity:1;transform:none}}' +
    '@keyframes cpt-splash-dot{0%,80%,100%{opacity:.25;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}' +
    '@keyframes cpt-splash-label{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}' +
    '.cpt-splash{position:fixed;inset:0;z-index:2147483000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;transition:opacity ' +
    OUT_MS +
    'ms ease}' +
    '.cpt-splash.out{opacity:0}' +
    '.cpt-splash-pair{display:flex;align-items:center;gap:14px;transition:transform ' +
    OUT_MS +
    'ms ease}' +
    '.cpt-splash.out .cpt-splash-pair{transform:scale(1.06)}' +
    '.cpt-splash-tile{width:60px;height:60px;border-radius:16px;background:#fff;display:flex;align-items:center;justify-content:center;overflow:hidden;box-shadow:0 1px 2px rgba(14,17,22,.08),0 10px 28px -14px rgba(14,17,22,.45)}' +
    '.cpt-splash-tile.l{animation:cpt-splash-left .38s cubic-bezier(.2,.8,.2,1) both}' +
    '.cpt-splash-tile.r{animation:cpt-splash-right .38s cubic-bezier(.2,.8,.2,1) both}' +
    '.cpt-splash-dots{display:flex;gap:5px}' +
    '.cpt-splash-dots span{width:6px;height:6px;border-radius:50%;animation:cpt-splash-dot 1.1s ease-in-out infinite both}' +
    '.cpt-splash-dots span:nth-child(2){animation-delay:.15s}.cpt-splash-dots span:nth-child(3){animation-delay:.3s}' +
    '.cpt-splash-label{font:600 14px/1.3 "Open Sans",system-ui,sans-serif;color:#32325d;animation:cpt-splash-label .4s .12s ease-out both}';
  document.head.appendChild(style);
}

/** The app as the rail knows it (name, colour, icon pixels) — there before the app list loads. */
function useCatalogEntry(itemId) {
  const [entry, setEntry] = useState(null);
  useEffect(() => {
    try {
      // eslint-disable-next-line no-undef
      const area = typeof chrome !== 'undefined' && chrome.runtime?.id ? chrome.storage?.local : null;
      if (!area) return;
      area.get(CATALOG_KEY, (result) => {
        const items = result?.[CATALOG_KEY]?.items;
        setEntry(Array.isArray(items) ? items.find((i) => i.id === itemId) || null : null);
      });
    } catch (e) {
      /* no extension storage: the store's copy fills in */
    }
  }, [itemId]);
  return entry;
}

/** Raw RGBA pixels (from the catalog) painted onto a canvas. */
function PixelIcon({ pixels }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !pixels?.data) return;
    try {
      const bytes = Uint8ClampedArray.from(atob(pixels.data), (c) => c.charCodeAt(0));
      canvas.getContext('2d')?.putImageData(new ImageData(bytes, pixels.size, pixels.size), 0, 0);
    } catch (e) {
      /* leaves the tile blank */
    }
  }, [pixels]);
  return <canvas ref={ref} width={pixels.size} height={pixels.size} style={{ width: 60, height: 60, display: 'block' }} />;
}

export default function AppSplash({ appId }) {
  const store = useVendorStore();
  const catalog = useCatalogEntry(`app:${appId}`);
  const app = store.getApp(appId);
  const [phase, setPhase] = useState('in'); // in → out → gone
  const startedAt = useRef(Date.now());

  ensureStyle();

  // Ready: this app's frame is the one on screen and has loaded (or said hello).
  const status = store.statusFor(appId);
  const ready = store.activeAppId === appId && Boolean(status.frameLoadedAt || status.helloAt);

  useEffect(() => {
    if (phase !== 'in') return undefined;
    const lift = () => setPhase('out');
    if (ready) {
      const t = setTimeout(lift, Math.max(0, MIN_MS - (Date.now() - startedAt.current)) + 120); // + a beat for the frame to be placed
      return () => clearTimeout(t);
    }
    const t = setTimeout(lift, Math.max(0, MAX_MS - (Date.now() - startedAt.current)));
    return () => clearTimeout(t);
  }, [ready, phase]);

  useEffect(() => {
    if (phase !== 'out') return undefined;
    const t = setTimeout(() => setPhase('gone'), OUT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === 'gone') return null;

  const name = app?.name || catalog?.label || 'app';
  const color = app?.color || catalog?.color || '#5e72e4';
  return (
    <div
      className={`cpt-splash${phase === 'out' ? ' out' : ''}`}
      role="status"
      aria-live="polite"
      style={{ background: `radial-gradient(120% 80% at 50% 42%, ${color}1f, transparent 70%), #ffffff` }}
    >
      <div className="cpt-splash-pair">
        <span className="cpt-splash-tile l">
          <img src={logo} alt="Comprehend" style={{ width: 40, height: 40, objectFit: 'contain', display: 'block' }} />
        </span>
        <span className="cpt-splash-dots" aria-hidden="true">
          <span style={{ background: color }} />
          <span style={{ background: color }} />
          <span style={{ background: color }} />
        </span>
        <span className="cpt-splash-tile r" style={{ background: app?.icon || catalog?.iconPixels ? '#fff' : color }}>
          {app?.icon ? (
            <img src={app.icon} alt="" style={{ width: 60, height: 60, objectFit: 'cover', display: 'block' }} />
          ) : catalog?.iconPixels ? (
            <PixelIcon pixels={catalog.iconPixels} />
          ) : (
            <span style={{ color: '#fff', font: '700 24px/1 system-ui, sans-serif' }}>{name.slice(0, 1).toUpperCase()}</span>
          )}
        </span>
      </div>
      <div className="cpt-splash-label">Opening {name}…</div>
    </div>
  );
}
